import React, {useCallback, useMemo, useState} from 'react'
import {sha256} from 'js-sha256'
import {useScript, appleAuthHelpers} from 'react-apple-signin-auth'
import styled from 'styled-components'
import {useForm} from 'react-hook-form'
import {AUTH_LOGIN_MAX_ATTEMPT, WINDOW_MODE_MOBILE_WIDTH} from 'consts'
import {useTranslation} from 'i18n'
import {AuthRegisterWaitingModal, GiftShopTemplateModalMFA} from 'pages'
import {requestData} from 'services'
import {
  getAppleSSOClientId,
  getAppleSSORedirectUri,
  showBanModal,
  showSnackbar,
  useHistory,
} from 'utils'
import {
  AuthFormSimpleRegisterData,
  AuthLoginResponse,
  AuthLoginUnverifiedResponse,
  AuthSSOAppleResponse,
} from 'types'
import {Button, Icon} from 'common/components'
import convertUnit from 'lib/unit'
import {useDispatch, useSelector} from 'lib/redux'
import {TemplateAuthAccessUsernameModal} from '../../../../template'
import {AuthLoginAppleProps} from './AuthLoginAppleProps'

const StyledButtonLoginApple = styled(Button)`
  ${({theme}) => ({borderColor: theme.white_3})}
  border-style: solid;
  border-width: ${convertUnit(1)};
  display: flex;
  flex: 1;
  @media (max-width: ${WINDOW_MODE_MOBILE_WIDTH}px) {
    margin-left: 0;
  }
`

const StyledIcon = styled(Icon)`
  margin-right: ${convertUnit(15)};
`

export default function AuthLoginApple({
  label,
  buttonStyle,
  shouldRedirect = true,
  onClose,
}: AuthLoginAppleProps) {
  const {translate} = useTranslation()
  const history = useHistory()
  const [loading, setLoading] = useState(false)
  const [waitingModal, setWaitingModal] = useState(false)
  const {tag_id, tree_id} = useSelector('treeQrState') || {}
  const {update} = useDispatch()
  const form = useForm<AuthFormSimpleRegisterData>({
    defaultValues: {
      username: '',
    },
  })
  const {setError} = form
  const stateModalUsername = useState(false)
  const stateModalUsernameLoading = useState(false)
  const [ssoToken, setSsoToken] = useState('')
  const stateModalMFA = useState(false)
  const setModalMFA = stateModalMFA[1]
  const stateMFALoading = useState(false)
  const setMFALoading = stateMFALoading[1]
  const stateMFAError = useState('')
  const setMFAError = stateMFAError[1]

  const handleRenderWaitingModal = useMemo(
    () => (
      <AuthRegisterWaitingModal
        visible={waitingModal}
        toggleModal={() => setWaitingModal(false)}
      />
    ),
    [waitingModal],
  )

  const handleFailed = useCallback(() => {
    setLoading(false)
    showSnackbar(translate('giftShop:snackbarFailedExecute'))
  }, [translate])

  const handleEasySignupLogin = useCallback(
    (tokenId: string, username?: string, totp?: string) => {
      if (username) {
        stateModalUsernameLoading[1](true)
      } else if (totp) {
        setMFALoading(true)
      }
      requestData('auth_easy_signup_login_sso_apple', {
        data: {token: tokenId, username, mfa_code: totp},
        onRequestReceived: () => {
          stateModalUsernameLoading[1](false)
          setMFALoading(false)
        },
        onRequestSuccess: ({data, status}) => {
          if (status === 200) {
            setModalMFA(false)
            setMFAError('')
            const userInfo = data.result as AuthLoginResponse
            update('user', userInfo)
            update('lastUserState', {
              username: undefined,
              photo: undefined,
              refreshToken: undefined,
            })
            if (tree_id && tag_id) {
              history.pushQuery({
                path: 'tree_events_bib_masonry',
                state: {
                  tree_url: '',
                  tree_name: '',
                  fromScan: true,
                },
                queryObject: {
                  tree_id,
                  tag_id,
                  search: '',
                },
              })
              return
            }
            if (shouldRedirect) {
              const {search} = history.location
              if (search.startsWith('?next')) {
                if (history.length > 1) {
                  history.goBack()
                } else {
                  window.location.replace(decodeURIComponent(search))
                }
                return
              }
              location.reload()
            } else {
              onClose && onClose()
            }
          } else if (status === 202) {
            const result = data.result as AuthLoginUnverifiedResponse
            if (result.child_consent_code) {
              history.push('auth_easy_signup_underage', {
                child_consent_code: data.result.child_consent_code,
                token: tokenId,
                method: 'apple',
              })
            } else if (result.recovery_code) {
              const {recovery_code, remaining_time, member_id} = result
              history.push('auth_recover_account', {
                recovery_code,
                remaining_time,
                member_id,
                token: tokenId,
                method: 'apple',
              })
            } else {
              history.push('auth_easy_signup_sso', {
                token: tokenId,
                ssoMethod: 'apple',
                suggestions: result.suggestions,
              })
            }
          } else if (status === 400 && data.detail) {
            if (data.detail.username === 'invalid') {
              setError('username', {
                type: 'unavailable',
                message: translate('auth:registerEasySignupUsernameNotMatch'),
              })
            } else if (data.detail.mfa_attempt) {
              setMFAError(
                translate('giftShop:settingAuthAppInvalidError', {
                  count:
                    AUTH_LOGIN_MAX_ATTEMPT -
                    parseInt(data.detail.mfa_attempt, 10),
                }),
              )
            } else if (data.detail.mfa_code === 'required') {
              setModalMFA(true)
            }
          } else if (status === 401) {
            stateModalUsername[1](true)
          } else if (
            status === 423 &&
            data.message === 'feature suspended' &&
            data.detail
          ) {
            setModalMFA(false)
            setMFAError('')
            showBanModal(status, 'Attempt Login Suspended')
          } else if (status === 503) {
            setModalMFA(false)
            setMFAError('')
            setWaitingModal(true)
          } else {
            setModalMFA(false)
            setMFAError('')
          }
        },
        onRequestFailed: handleFailed,
      })
    },
    [
      handleFailed,
      history,
      onClose,
      setError,
      setMFAError,
      setMFALoading,
      setModalMFA,
      shouldRedirect,
      stateModalUsername,
      stateModalUsernameLoading,
      tag_id,
      translate,
      tree_id,
      update,
    ],
  )

  const handleSuccess = useCallback(
    (response: AuthSSOAppleResponse) => {
      const {id_token} = response.authorization
      setSsoToken(id_token)
      handleEasySignupLogin(id_token)
    },
    [handleEasySignupLogin],
  )

  const handleClick = useCallback(async () => {
    setLoading(true)
    appleAuthHelpers.signIn({
      authOptions: {
        clientId: getAppleSSOClientId(),
        redirectURI: getAppleSSORedirectUri(),
        responseType: 'ALL',
        scope: 'email name',
        nonce: sha256('nonce'),
        state: 'state',
        usePopup: true,
      },
      onError: handleFailed,
      onSuccess: (res) => handleSuccess(res),
    })
  }, [handleFailed, handleSuccess])

  const handleRenderAppleButton = useMemo(
    () => (
      <StyledButtonLoginApple
        onClick={handleClick}
        label={label}
        leftIcon={<StyledIcon type="apple" />}
        backgroundColor="white_3"
        backgroundHoverColor="white_3"
        color="black"
        isLoading={loading}
        disabled={loading}
        style={buttonStyle}
      />
    ),
    [buttonStyle, handleClick, label, loading],
  )

  const handleRenderModalUsername = useMemo(
    () => (
      <TemplateAuthAccessUsernameModal
        stateVisible={stateModalUsername}
        stateLoading={stateModalUsernameLoading}
        form={form}
        onClick={(username) => handleEasySignupLogin(ssoToken, username)}
      />
    ),
    [
      form,
      handleEasySignupLogin,
      ssoToken,
      stateModalUsername,
      stateModalUsernameLoading,
    ],
  )

  const handleRenderModalMFA = useMemo(
    () => (
      <GiftShopTemplateModalMFA
        onConfirm={(code) => handleEasySignupLogin(ssoToken, undefined, code)}
        stateError={stateMFAError}
        stateLoading={stateMFALoading}
        stateModal={stateModalMFA}
      />
    ),
    [
      handleEasySignupLogin,
      ssoToken,
      stateMFAError,
      stateMFALoading,
      stateModalMFA,
    ],
  )

  useScript(appleAuthHelpers.APPLE_SCRIPT_SRC)

  return (
    <>
      {handleRenderWaitingModal}
      {handleRenderModalMFA}
      {handleRenderModalUsername}
      {handleRenderAppleButton}
    </>
  )
}
